import type { FC, ReactNode } from 'react'
import { useLocation } from 'react-router'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import {
	ArrowLeftIcon,
	Home03Icon,
	MarkerPin01Icon
} from '@/components/ui/icons'
import type {
	MMSubmissionInventory,
	SubmissionSource
} from '@/features/luxe-submissions/types'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import { mmInventoryToBadgeStyle, mmInventoryToLabelTKey } from '../utils'
import { SourceBadge } from './source-badge'

interface ListingDetailsHeaderBaseProps {
	title: string
	listingId?: string
	listingLink?: string | null
	location?: string | null
	source?: SubmissionSource
	inventory?: MMSubmissionInventory | null
	// Rendered after the source and inventory badges
	score?: ReactNode
	actions?: ReactNode
	backUrl?: string
}

export const ListingDetailsHeaderBase: FC<ListingDetailsHeaderBaseProps> = ({
	title,
	listingId,
	listingLink,
	location,
	source,
	inventory,
	score,
	actions,
	backUrl
}) => {
	const { t } = useTranslation()
	const { state } = useLocation()
	return (
		<div className='border-neutral-200 border-b px-10 pt-6 pb-6'>
			<div className='flex items-center gap-6'>
				<ButtonLinkLegacy
					className='size-11 shrink-0 px-0!'
					shape='rounded'
					to={{
						pathname: backUrl,
						search: state?.fromSearch
					}}
					variant='icon'
				>
					<ArrowLeftIcon size={20} />
				</ButtonLinkLegacy>
				<div className='flex flex-col gap-2 text-neutral-900'>
					<h1 className='font-medium text-lg'>{title}</h1>
					<div className='flex items-center gap-6'>
						{listingId && (
							<span
								className={cn(
									'flex items-center gap-1.5 font-medium text-neutral-600 text-sm',
									listingLink && 'cursor-pointer underline'
								)}
							>
								<Home03Icon size={20} />
								<a
									href={listingLink ?? undefined}
									rel='noopener noreferrer'
									target='_blank'
								>
									#{listingId}
								</a>
							</span>
						)}

						{location && (
							<span className='flex items-center gap-1.5 text-neutral-600 text-sm'>
								<MarkerPin01Icon size={20} />
								{location}
							</span>
						)}

						{source && <SourceBadge source={source} />}
						{inventory && (
							<span
								className={`inline-flex rounded-full border px-2 py-0.5 font-medium text-xs ${mmInventoryToBadgeStyle[inventory]}`}
							>
								{t(mmInventoryToLabelTKey[inventory])}
							</span>
						)}
						{score}
					</div>
				</div>

				{actions && (
					<div className='ml-auto flex items-center gap-3'>
						{actions}
					</div>
				)}
			</div>
		</div>
	)
}
